import { db } from "@/firebase/init.js";
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  orderBy,
  query,
  // Pagination
  limit,
  startAfter,
  where,
} from "firebase/firestore"; // Create, Add Collection

const examsModule = {
  namespaced: true, // automatically added
  state: {
    examsList: [],
    exam: {},
    isLoading: true,
    isFirstLoad: true,
    // Exam Results
    students: [],
    // Pagination
    loadingMore: false,
    showLoadingButton: true,
    lastVisible: "",
    orderBy: "created_date",
    sortBy: "desc",
    // Search
    searchResult: [],
  },

  getters: {
    getExams: (state) => state.examsList,
    isLoading: (state) => state.isLoading,
    isFirstLoad: (state) => state.isFirstLoad,
    // Exam Results
    getStudents: (state) => state.students,
    // Pagination
    isLoadingMore: (state) => state.loadingMore,
    isShowLoadingButton: (state) => state.showLoadingButton,
    // Search
    getSearchResult: (state) => state.searchResult,
  },
  mutations: {
    // Loading
    setLoading: (state, payload) => {
      state.isLoading = payload;
    },

    // First load
    setFirstLoad: (state, payload) => {
      state.isFirstLoad = payload;
    },
    // Get All Exams
    setExams: (state, payload) => {
      state.examsList = payload;
    },
    // Add Exam
    addExam: (state, payload) => {
      if (state.examsList.length > 0) state.examsList.unshift(payload);
      else state.examsList = [payload];
    },

    // Delete Exam
    deleteExam: (state, payload) => {
      console.log("payload", payload);
      let filtered = state.examsList.filter(function (value) {
        return value.exam_id != payload;
      });

      state.examsList = filtered;
    },
    // Update Exam
    updateExam: (state, payload) => {
      state.examsList[payload.index] = payload.exam;
    },
    // Get Exam by Id
    getExamById: (state, payload) => {
      state.exam = state.examsList.find((exam) => exam.exam_id == payload);
    },
    //---------------------------------------------------
    // Pagination and Sort and Search
    // Get Search Result
    setSearchResult: (state, payload) => {
      state.searchResult = payload;
    },
    setData: (state, payload) => {
      state.students = payload;
    },
    setLastVisible: (state, payload) => {
      state.lastVisible = payload;
    },
    setOrderBy: (state, payload) => {
      state.orderBy = payload;
    },
    setSortBy: (state, payload) => {
      state.sortBy = payload;
    },

    setNextData: (state, payload) => {
      state.students.push(...payload);
    },
    setLoadingMore: (state, payload) => {
      state.loadingMore = payload;
    },
    updateStudentResult: (state, payload) => {
      const index = state.students.findIndex(
        (student) => student.student_id === payload.studentId
      );
      if (index === -1) return;
      state.students[index] = { ...state.students[index], ...payload };
    },
    setShowLoadingButton: (state, payload) => {
      state.showLoadingButton = payload;
    },
  },
  actions: {
    // Get All Exams
    async fetchExams({ commit }) {
      commit("setLoading", true);
      commit("setFirstLoad", true);

      // No `where` filter here: exams created from a lesson are written
      // without a `categories` field, and a "!=" filter silently drops every
      // document that is missing the field, hiding them from this list.
      const examsList = await getDocs(query(collection(db, "Exams")));
      let examsArray = [];
      if (!examsList.empty) {
        examsList.forEach((doc) => {
          examsArray.push({ ...doc.data(), exam_id: doc.id });
        });
      }
      commit("setExams", examsArray);
      commit("setLoading", false);
      commit("setFirstLoad", false);

      return examsArray;
    },
    // Add Exam
    async addExam({ commit }, payload) {
      // const colRef = collection(db, "Categories");
      const documentReference = doc(collection(db, "Exams"));
      const exam = {
        ...payload,
        exam_id: documentReference.id,
        created_date: new Date(),
      };
      await setDoc(documentReference, exam);
      commit("addExam", exam);
      return exam;
    },
    // Update Exam
    async updateExam({ commit }, payload) {
      // Exams created from a lesson carry `id` instead of `exam_id`.
      const examId = payload.exam_id ?? payload.id;
      if (!examId) {
        throw new Error("updateExam called without an exam id");
      }
      await updateDoc(doc(db, "Exams", examId), payload);
      commit("updateExam", payload);
      return payload;
    },

    // ---- Manual grading of essay answers ----

    // One student's submitted result: Exams/{examId}/results/{studentId}
    async getStudentResult(_, { examId, studentId }) {
      const snapshot = await getDoc(
        doc(db, "Exams", examId, "results", studentId)
      );
      if (!snapshot.exists()) return null;
      return { ...snapshot.data(), student_id: snapshot.id };
    },

    // Writes the teacher's marks back onto the result.
    // `questions` carries degree_awarded per question; `essayDegree` is their
    // sum; `status` flips to "graded" once nothing is left to mark.
    async gradeStudentResult(
      { commit },
      { examId, studentId, questions, essayDegree, status }
    ) {
      if (!examId || !studentId) {
        throw new Error("gradeStudentResult called without ids");
      }

      const update = {
        questions,
        essay_degree: essayDegree,
        status,
        graded_date: new Date(),
      };

      await updateDoc(doc(db, "Exams", examId, "results", studentId), update);
      commit("updateStudentResult", { studentId, ...update });
      return update;
    },

    // Get Exam by id
    async getExamById({ commit }, payload) {
      commit("setLoading", true);

      let exam = await getDoc(doc(db, "Exams", payload));
      commit("getExamById", exam.data());
      commit("setLoading", false);

      return exam.data();
    },

    // Delete Exams
    async deleteExam({ commit }, payload) {
      deleteDoc(doc(db, "Exams", payload));
      commit("deleteExam", payload);
    },

    // Students Grades
    changeOrderBy({ commit }, payload) {
      commit("setOrderBy", payload);
    },
    changeSortBy({ commit }, payload) {
      commit("setSortBy", payload);
    },

    //---- Pagination
    async getFirstData({ commit }, payload) {
      commit("setLoading", true);
      commit("setFirstLoad", true);
      const data = await getDocs(
        query(
          collection(db, `Exams/${payload}/results`),
          orderBy(
            this.state.examsModule.orderBy,
            this.state.examsModule.sortBy
          ),
          limit(10)
        )
      );

      const lastVisible = data.docs[data.docs.length - 1] || null;
      commit("setLastVisible", lastVisible);
      let dataArray = [];
      if (!data.empty) {
        data.forEach((doc) => {
          dataArray.push({ student_id: doc.id, ...doc.data() });
        });
      } else {
        dataArray = false;
      }
      if (data.size >= 10) {
        commit("setShowLoadingButton", true);
      } else {
        commit("setShowLoadingButton", false);
      }
      commit("setData", dataArray);
      commit("setLoading", false);
      commit("setFirstLoad", false);
    },

    async getNextData({ commit }, payload) {
      commit("setLoadingMore", true);

      const data = await getDocs(
        query(
          collection(db, `Exams/${payload}/results`),
          orderBy(
            this.state.examsModule.orderBy,
            this.state.examsModule.sortBy
          ),
          limit(10),
          startAfter(this.state.examsModule.lastVisible)
        )
      );
      const lastVisible = data.docs[data.docs.length - 1] || null;
      const firstVisible = data.docs[0] || null;

      commit("setLastVisible", lastVisible);
      commit("setFirstVisible", firstVisible);

      let dataArray = [];
      if (!data.empty) {
        data.forEach((doc) => {
          dataArray.push({ student_id: doc.id, ...doc.data() });
        });
      }
      commit("setNextData", dataArray);

      if (data.size >= 10) {
        commit("setShowLoadingButton", true);
      } else {
        commit("setShowLoadingButton", false);
      }
      commit("setLoadingMore", false);
    },
    // ----- Search
    async searchData({ commit }, payload) {
      commit("setFirstLoad", false);
      const data = await getDocs(
        query(
          collection(db, `Exams/${payload.id}/results`),
          where("user.user_name", ">=", payload.searchValue),
          limit(10)
        )
      );
      let dataArray = [];
      data.forEach((doc) => {
        dataArray.push({ code_id: doc.id, ...doc.data() });
      });
      commit("setSearchResult", dataArray);
    },
  },
};

export default examsModule;
